import React, { useEffect, useState } from 'react';
import './AffiliateTrackingPage.scss';
import Typography from '@material-ui/core/Typography';
import Breadcrumbs from '@material-ui/core/Breadcrumbs';
import CircularProgress from '@material-ui/core/CircularProgress';
import { http, localstorage, useNavigationService } from '../../fx-core';

const pick = (obj: any, ...keys: string[]) => {
  for (const k of keys) {
    if (obj[k] !== undefined && obj[k] !== null) return obj[k];
    const found = Object.keys(obj).find((ok) => ok.toLowerCase() === k.toLowerCase());
    if (found && obj[found] !== null) return obj[found];
  }
  return undefined;
};

const getEventLabel = (eventType: string, isMerchant: boolean) => {
  const labels: { [key: string]: { merchant: string; reseller: string; icon: string } } = {
    Requested: { merchant: 'Request Received', reseller: 'Request Sent', icon: 'fa-paper-plane' },
    Accepted: { merchant: 'You Accepted', reseller: 'Accepted by Merchant', icon: 'fa-check-circle' },
    Rejected: { merchant: 'You Rejected', reseller: 'Rejected by Merchant', icon: 'fa-times-circle' },
    ResellerNotified: { merchant: 'Reseller Notified', reseller: 'You Were Notified', icon: 'fa-bell' },
    AddedToStall: { merchant: 'Reseller Added to Their Stall', reseller: 'You Added to Your Stall', icon: 'fa-store' },
    Live: { merchant: 'Now Live — Reseller is Selling', reseller: 'Now Live — Start Promoting', icon: 'fa-rocket' },
    Sold: { merchant: 'Your Product Sold!', reseller: 'Sale Made — Order Placed', icon: 'fa-check-double' },
  };
  const entry = labels[eventType];
  if (!entry) return { label: eventType, icon: 'fa-circle' };
  return { label: isMerchant ? entry.merchant : entry.reseller, icon: entry.icon };
};

const HAPPY_PATH = ['Accepted', 'ResellerNotified', 'AddedToStall', 'Live'];

const AffiliateTrackingPage = (props: any) => {
  const { navigate } = useNavigationService();
  const affiliateProductId = props.match?.params?.id;
  const currentUserId = parseInt(localstorage.getItem('userId') || '0');

  const [timeline, setTimeline] = useState<any[]>([]);
  const [productInfo, setProductInfo] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  const fetchTracking = () => {
    setLoading(true);
    http.get({ url: `affiliate-product/${affiliateProductId}/tracking` }).then((res: any) => {
      setTimeline(res.data || []);
      setLoading(false);
    }).catch(() => {
      setTimeline([]);
      setLoading(false);
    });
  };

  const fetchDetails = () => {
    http.get({ url: `affiliate-product/${affiliateProductId}/details` }).then((res: any) => {
      setProductInfo(res.data);
    }).catch(() => setProductInfo(null));
  };


  useEffect(() => {
    if (affiliateProductId) {
      fetchTracking();
      fetchDetails();
    }
  }, [affiliateProductId]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

 const navToDashboard = () => {
  navigate({
    targetRoute: '/provider/affiliatedashboard',
    targetTitle: 'Affiliate Dashboard',
    targetData: {},
    sourceState: {},
    eventSource: ''
  });
};

  const isMerchantView = productInfo
    ? currentUserId === pick(productInfo, 'merchantId')
    : false;
  const isResellerView = productInfo
    ? currentUserId === pick(productInfo, 'resellerId')
    : false;

  const loggedEvents: { [key: string]: any } = {};
  timeline.forEach((evt) => {
    const type = pick(evt, 'eventType');
    if (type) loggedEvents[type] = evt;
  });

  const wasRejected = !!loggedEvents['Rejected'];
  const wasSold = !!loggedEvents['Sold'];

  const stepsToRender = wasRejected
    ? ['Accepted', 'Rejected']
    : wasSold
      ? [...HAPPY_PATH, 'Sold']
      : HAPPY_PATH;

  const lastCompletedIndex = stepsToRender.reduce((lastIdx, type, idx) => {
    return loggedEvents[type] ? idx : lastIdx;
  }, -1);

  // Product card fields (case-insensitive)
  const itemName = productInfo ? pick(productInfo, 'itemName') : null;
  const itemImagePath = productInfo ? pick(productInfo, 'itemImagePath') : null;
  const marginNumber = productInfo ? pick(productInfo, 'marginNumber') : null;
  const merchantName = productInfo ? pick(productInfo, 'merchantName', 'merchantStallName') : null;
  const resellerId = productInfo ? pick(productInfo, 'resellerId') : null;

  return (
    <div className="affiliatetracking-ui">
      <div style={{ height: '85px' }}></div>
      <div className="pt-3 px-4 breadcrumb-bar" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#f5f1e8' }}>
        <Breadcrumbs aria-label="breadcrumb">
          <span className="pointer breadcrumb-link" onClick={navToDashboard}>
            Dashboard
          </span>
          <Typography style={{ color: '#3c4858', fontWeight: 500 }}>Request Tracking</Typography>
        </Breadcrumbs>
      </div>

      <div className="px-4 py-4">
        {loading && (
          <div className="py-5 text-center"><CircularProgress /></div>
        )}

        {!loading && productInfo && (
          <div className="tracking-card mb-4">
            <div className="tracking-card-header">
              {itemImagePath && (
                <img
                  className="tracking-thumb"
                  src={itemImagePath}
                  alt={itemName}
                />
              )}
              <div>
                <div className="font-20"><b>{itemName}</b></div>
                <div className="pt-1 font-14 text-muted">
                  {isMerchantView && `Requested by Reseller #${resellerId}`}
                  {!isMerchantView && isResellerView && `From: ${merchantName}`}
                </div>
                <div className="pt-1 font-14 text-muted">
                  Commission: <b>{marginNumber}%</b>
                </div>
              </div>
            </div>
          </div>
        )}

        {!loading && (
          <div className="timeline-wrapper">
            {timeline.length === 0 && (
              <div className="py-4 text-center font-14 text-muted">No tracking events yet.</div>
            )}
            {stepsToRender.map((stepType, i) => {
              const evt = loggedEvents[stepType];
              const meta = getEventLabel(stepType, isMerchantView);
              const isCompleted = !!evt;
              const isLast = i === stepsToRender.length - 1;
              const isRejectedStep = stepType === 'Rejected' && isCompleted;
              const lineIsActive = i < lastCompletedIndex;
              const isCurrent = i === lastCompletedIndex;

             return (
  <div
    className={`timeline-step ${isCompleted ? 'is-completed' : 'is-pending'} ${isCurrent ? 'is-current' : ''} ${isRejectedStep ? 'is-rejected' : ''}`}
    key={stepType}
  >
                  <div className="timeline-icon-col">
                    <div className="timeline-icon">
                      <i className={`fas ${meta.icon}`}></i>
                      {isCurrent && <span className="pulse-ring"></span>}
                    </div>
                    {!isLast && <div className={`timeline-line ${lineIsActive ? 'is-active' : ''}`}></div>}
                  </div>
                  <div className="timeline-content">
                    <div className="font-16 step-label"><b>{meta.label}</b></div>
                    <div className="font-13 text-muted">
                      {isCompleted
                        ? new Date(pick(evt, 'eventDate', 'createdDate')).toLocaleString()
                        : 'Pending'}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default AffiliateTrackingPage;
